import type { AgentConnection, InvitationLink, Role } from '@mystiq/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, useWorkspaceId, useWorkspaceInfo, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { CopyField, EmptyState, ErrorLine, Thumb } from '../components/ui.tsx';
import { WorkspaceStatusSection } from '../components/WorkspaceStatus.tsx';
import { Agents, ICON, Key, Members } from '../icons.ts';
import { timeAgo, timeUntil } from '../lib/format.ts';

function connectionMeta(connection: AgentConnection, showOwner: boolean): string {
  const parts = [
    connection.authKind === 'token' ? `Key ending …${connection.tokenHint ?? ''}` : 'Signed-in client',
    showOwner && connection.userDisplayName ? `for ${connection.userDisplayName}` : null,
    `Connected ${timeAgo(connection.createdAt)}`,
    connection.lastUsedAt ? `last used ${timeAgo(connection.lastUsedAt)}` : 'not used yet',
  ];
  return parts.filter(Boolean).join(' · ');
}

const ROLE_WORD: Record<Role, string> = { owner: 'an owner', editor: 'an editor', viewer: 'a viewer' };

export function AgentsPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const info = useWorkspaceInfo(ws);
  const setup = useQuery({ queryKey: keys.agentSetup(ws), queryFn: () => api.agentSetup(ws) });
  const connections = useQuery({ queryKey: keys.connections(ws), queryFn: () => api.connections(ws) });
  const [clientName, setClientName] = useState('');
  const [created, setCreated] = useState<{ key: string; name: string } | null>(null);
  const create = useWorkspaceMutation(ws, (name: string) => api.createConnectionKey(ws, name));
  const revoke = useWorkspaceMutation(ws, (id: string) => api.revokeConnection(ws, id));
  const allow = useWorkspaceMutation(ws, (id: string) => api.allowConnection(ws, id));
  const role = info.data?.role;
  const showOwner = role === 'owner';
  const mcpUrl = setup.data?.mcpUrl ?? '';

  return (
    <div className="page">
      <h1 className="page-title">Connected agents</h1>
      <p style={{ marginTop: 'var(--space-6)', maxWidth: 720 }}>
        Agents such as Claude, Cursor or your own tools can read and change this workspace through MCP. They act as you{role ? `, ${ROLE_WORD[role]}` : ''}, on the same saved content you see here, without asking you to approve each action. Disconnect an agent at any time.
      </p>

      <section className="section" aria-labelledby="connect-heading">
        <h2 className="section-heading" id="connect-heading">Connect an agent</h2>
        <div className="stack">
          {mcpUrl && <CopyField label="MCP server URL for this workspace" value={mcpUrl} />}
          {setup.data?.oauthAvailable ? (
            <p className="meta">In clients that support sign-in, add this URL and sign in with your Mystiq account when asked.</p>
          ) : (
            <p className="meta">Sign-in for MCP clients appears here once Clerk is configured. Until then, use a connection key.</p>
          )}
          <form
            className="inline-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const name = clientName.trim();
              if (!name) return;
              const result = await create.mutateAsync(name).catch(() => null);
              if (result) {
                setCreated({ key: result.key, name });
                setClientName('');
              }
            }}
          >
            <input className="input" aria-label="Agent name" placeholder="Agent name, e.g. Claude Code on my laptop" value={clientName} onChange={(event) => setClientName(event.target.value)} />
            <button className="button button-primary" type="submit" disabled={!clientName.trim() || create.isPending}>
              <Key size={ICON.nav} stroke={ICON.stroke} />
              Create connection key
            </button>
          </form>
          <ErrorLine error={create.error} />
          {created && (
            <div className="conflict">
              <p>
                <span className="state">Copy this key now.</span> It is shown once. Anyone with it can act as you in this workspace until you disconnect it.
              </p>
              <CopyField label={`Key for ${created.name}`} value={created.key} />
              <CopyField label="Claude Code" value={`claude mcp add --transport http mystiq ${mcpUrl} --header "Authorization: Bearer ${created.key}"`} />
              <div>
                <button className="button button-inline" onClick={() => setCreated(null)}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="section" aria-labelledby="connections-heading">
        <h2 className="section-heading" id="connections-heading">{showOwner ? 'All connections' : 'Your connections'}</h2>
        <ErrorLine error={revoke.error ?? allow.error} />
        <ul className="rows">
          {connections.data?.map((connection) => (
            <li className="row" key={connection.id}>
              <Thumb icon={Agents} />
              <span className="row-body">
                <span className="row-title">{connection.clientName}</span>
                <span className="row-meta">
                  {connection.status === 'revoked' && <span className="state">Disconnected. </span>}
                  {connectionMeta(connection, showOwner)}
                </span>
              </span>
              {connection.status === 'active' ? (
                <button className="button button-inline" onClick={() => revoke.mutate(connection.id)}>
                  Disconnect
                </button>
              ) : connection.authKind === 'oauth' && connection.userId === info.data?.actor.userId ? (
                <button className="button button-inline" onClick={() => allow.mutate(connection.id)}>
                  Allow again
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {connections.isSuccess && !connections.data.length && <EmptyState icon={Agents} title="No agents connected yet" />}
      </section>
    </div>
  );
}

export function MembersPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const info = useWorkspaceInfo(ws);
  const members = useQuery({ queryKey: keys.members(ws), queryFn: () => api.members(ws) });
  const change = useWorkspaceMutation(ws, ({ userId, role }: { userId: string; role: Role }) => api.changeRole(ws, userId, role));
  const remove = useWorkspaceMutation(ws, (userId: string) => api.removeMember(ws, userId));
  const isOwner = info.data?.role === 'owner';
  const me = info.data?.actor.userId;
  return (
    <div className="page">
      <h1 className="page-title">Members</h1>
      <p className="meta" style={{ marginTop: 'var(--space-6)' }}>
        Owners manage members. Editors create and change content. Viewers read, search and download.
      </p>
      <div className="content stack">
        <ErrorLine error={change.error ?? remove.error} />
        <ul className="rows">
          {members.data?.map((member) => (
            <li className="row" key={member.userId}>
              <span className="thumb" aria-hidden style={{ borderRadius: '50%', background: 'var(--color-stone-700)', color: 'var(--color-white)', font: 'var(--text-label)' }}>
                {(member.displayName ?? member.email ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="row-body">
                <span className="row-title">
                  {member.displayName ?? member.email ?? 'Member'}
                  {member.userId === me ? ' (you)' : ''}
                </span>
                <span className="row-meta">{member.email ?? ''}</span>
              </span>
              {isOwner ? (
                <span className="row-actions">
                  <select
                    className="select"
                    aria-label={`Role for ${member.displayName ?? member.email}`}
                    value={member.role}
                    style={{ width: 'auto' }}
                    onChange={(event) => change.mutate({ userId: member.userId, role: event.target.value as Role })}
                  >
                    <option value="owner">Owner</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  {member.userId !== me && (
                    <button className="button button-inline" onClick={() => remove.mutate(member.userId)}>
                      Remove
                    </button>
                  )}
                </span>
              ) : (
                <span className="meta">{member.role[0]!.toUpperCase() + member.role.slice(1)}</span>
              )}
            </li>
          ))}
        </ul>
        {members.isSuccess && !members.data.length && <EmptyState icon={Members} title="No members" />}
      </div>
      {isOwner && <Invitations ws={ws} />}
      {isOwner && <WorkspaceStatusSection ws={ws} />}
    </div>
  );
}

const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', editor: 'Editor', viewer: 'Viewer' };

/** Owners invite people by link (Mystiq sends no email) and can withdraw open invitations. */
function Invitations({ ws }: { ws: string }) {
  const api = useApi();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [created, setCreated] = useState<InvitationLink | null>(null);
  const invitations = useQuery({ queryKey: keys.invitations(ws), queryFn: () => api.invitations(ws) });
  const invite = useWorkspaceMutation(ws, (input: { email: string; role: Role }) => api.invite(ws, input));
  const withdraw = useWorkspaceMutation(ws, (id: string) => api.revokeInvitation(ws, id));
  const open = (invitations.data ?? []).filter((i) => i.status === 'pending');
  return (
    <section className="section" aria-labelledby="invite-heading">
      <h2 className="section-heading" id="invite-heading">Invite people</h2>
      <div className="stack">
        <p className="meta">Mystiq makes a link for you to send. It works once, for 7 days, for someone signed in with that email address.</p>
        <form
          className="inline-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const link = await invite.mutateAsync({ email: email.trim(), role }).catch(() => null);
            if (link) {
              setCreated(link);
              setEmail('');
            }
          }}
        >
          <input className="input" type="email" required aria-label="Email address" placeholder="name@example.com" value={email} onChange={(event) => setEmail(event.target.value)} />
          <select className="select" aria-label="Role for the invited person" value={role} style={{ width: 'auto' }} onChange={(event) => setRole(event.target.value as Role)}>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
            <option value="owner">Owner</option>
          </select>
          <button className="button button-primary" type="submit" disabled={invite.isPending || !email.trim()}>
            Create invitation link
          </button>
        </form>
        <ErrorLine error={invite.error ?? withdraw.error} />
        {created && (
          <div className="conflict">
            <p>
              <span className="state">Send this link to {created.email}.</span> It is shown only now; you can always make a new one.
            </p>
            <CopyField label="Invitation link" value={created.url} />
            <div>
              <button className="button button-inline" onClick={() => setCreated(null)}>
                Done
              </button>
            </div>
          </div>
        )}
        {open.length > 0 && (
          <ul className="rows" aria-label="Open invitations">
            {open.map((invitation) => (
              <li className="row" key={invitation.id}>
                <span className="row-body">
                  <span className="row-title">{invitation.email}</span>
                  <span className="row-meta">
                    {ROLE_LABEL[invitation.role]} · {new Date(invitation.expiresAt) > new Date() ? `expires ${timeUntil(invitation.expiresAt)}` : 'expired'}
                  </span>
                </span>
                <button className="button button-inline" onClick={() => withdraw.mutate(invitation.id)} disabled={withdraw.isPending}>
                  Withdraw
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
